import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryState } from 'nuqs';
import { flowUrlParams } from '../../../../../../mini-apps/_shared/url-params';
import { toast } from 'sonner';
import { ANALYTICS_EVENTS } from '@shared/analytics/events';
import { signalActionRecordId } from '@shared/schemas/terra-signals';
import { SIGNAL_FIELDS, SIGNAL_PRESETS, SIGNAL_LANDING_PRESET_ID } from '@shared/flow/signal-view';
import type { FlowTableProps } from '../types';
import { useFlowIdentity } from '../useFlowIdentity';
import { useViewStateMachine } from '../views/useViewStateMachine';
import { useFlowViewState, useFlowViewStatePersist } from '../views/useViewState';
import { flowSignalsQueryKey, isUnexcludableFavoritesError, useFlowSignals, useSignalRow, useSignalsRanking } from './useSignalsQueries';
import { prioritizeSignals, type PrioritizedSignalRow } from './prioritizeSignals';
import { localTimeFor } from './localTime';
import { computeStepSince } from './stepSince';
import { arrangeSignalsClientSide, buildFieldComparator, signalClientGroupRank } from './arrangeSignalsClientSide';
import { signalGroupValue } from './signalGroupValue';
import { buildRankerLevelOverrides, groupingNeedsEveryPage } from './rankerGroupOrder';
import { applyClientSignalFilters, filterNeedsEveryPage } from './clientFilters';
import { buildCanonicalRanking } from './rankingFallback';
import { buildSignalsTableConfig, type SignalRowWithComputed } from './signalsTableConfig';
import { ALL_ELSE_UNEXCLUDABLE_TOAST, EXCLUDE_FAVORITES_SAVE_BLOCKED } from './chips/allElseCopy';
import { useSignalsUiStore } from './useSignalsUiStore';
import { useSlackClickAttribution } from './useSlackClickAttribution';
import { useTrackSignalsEvent } from './signalsAnalytics';
import { useRefreshOnMount } from './useRefreshOnMount';
import { useMinuteToken } from './useMinuteToken';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateSignalQueries } from './useSignalsMutations';
import { useAutoLoadAllPages, useReleaseAutoLoadedPages } from './useAutoLoadAllPages';
import { useSignalSelection } from './useSignalSelection';
import { useEffectiveFavoritesViewAs } from '../favorites/favorites-ui-store';
import { useFlowListLayout } from '../../../hooks/useFlowLayouts';
import { storedWidths, withDefaultWidths } from '../table-core/columns';
import { useFlowLayoutAwareMachine } from '../flow-layouts/useFlowLayoutAwareMachine';
import { identityFirst, mergeFixedColumns, restoreWorkingColumns, warnUnknownFixedKeys } from './mergeFixedColumns';
import { useNotesSummaries, type NoteSummaryItem } from '../shell-next/notes/useNotesSummaries';
import { signalNoteTarget } from '../shell-next/notes/signalNoteTarget';
import { isTombstonedAtStep, nextTombstoneExpiry, useTrellusHostStore, type TrellusTombstone } from '../../../lib/trellus/trellus-host-store';

/**
 * Headless model for the Signals tab's global mount (bead
 * terra-design-v2-table-tweaks-at4.17, S4-U1) — extracted verbatim (pure
 * move) out of `SignalsFlowTable.tsx`, which becomes a thin renderer over
 * this hook's return. See `account/useAccountTableModel.tsx` /
 * `contact/useContactTableModel.tsx` for the same `{ machine, tableProps }`
 * shape.
 *
 * The collapse block (renderer-owned chrome — V2 uses `useLedgerChrome`'s
 * instead) and `SignalsToolbar` stay in `SignalsFlowTable.tsx`; this hook
 * returns `groupLevels` so the collapse block can key off it.
 */

/**
 * Local calendar day, re-derived at the next local midnight — copied from
 * mini-apps/action-view/hooks/usePrioritizedActions.ts:16 (never imported,
 * mini-apps/CLAUDE.md) so the overdue tiebreak / due-date coloring stay
 * correct in a long-lived tab left open across midnight.
 */
function useLocalDayToken(): string {
  const [day, setDay] = useState(() => new Date().toDateString());
  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const t = setTimeout(() => setDay(new Date().toDateString()), nextMidnight.getTime() - now.getTime() + 1000);
    return () => clearTimeout(t);
  }, [day]);
  return day;
}

/** setTimeout's ceiling (a larger delay fires immediately); a farther expiry re-arms once this passes. */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * The clock reading (epoch ms) of the last time the earliest dated-snooze tombstone expired (0 before any), so the
 * list re-derives at the resurface date. At a REAL expiry it also invalidates the Signals queries (the same helper the
 * completion sync uses): the snooze made the server omit the row and the list never refetches on its own, so the cache
 * would otherwise keep missing it. A timer-ceiling re-arm is not an expiry and refetches nothing. Nothing lifetime-carrying lives in a ref (StrictMode-safe):
 * the effect re-reads the store value each run and the timeout clears in cleanup.
 */
function useTombstoneExpiryClock(tombstones: Readonly<Record<string, TrellusTombstone>>): number {
  const queryClient = useQueryClient();
  const [tickedAt, setTickedAt] = useState(0);
  useEffect(() => {
    const next = nextTombstoneExpiry(tombstones, Date.now());
    if (next === null) return;
    const delay = Math.min(Math.max(0, next - Date.now()) + 1, MAX_TIMER_MS);
    const t = setTimeout(() => {
      if (Date.now() >= next) void invalidateSignalQueries(queryClient).catch(() => undefined);
      setTickedAt(Date.now());
    }, delay);
    return () => clearTimeout(t);
  }, [tombstones, tickedAt, queryClient]);
  return tickedAt;
}

const NO_GROUP_LEVELS: string[] = [];
const NO_FIXED_COLUMNS: readonly string[] = [];
/** The table's real column keys — a layout's `fixedColumns` naming anything else is dropped (and warned). */
const SIGNAL_COLUMN_KEYS: ReadonlySet<string> = new Set(SIGNAL_FIELDS.map(f => f.key));
/** The identity CDR / GS freeze first (`signalsColumns.tsx` `identityKey`); Signal Response freezes nothing, so a view it reordered may hold this later. */
const SIGNAL_IDENTITY_COLUMN = 'contact_name';

export interface UseSignalTableModelOptions {
  fillHeight?: boolean;
  /**
   * V2 `SignalsTable` only (Signals V3 PR 2, U6): resolve the `signals` list
   * layout and render its `fixedColumns` first. V1 `SignalsFlowTable` never
   * passes it, so its machine, `visibleColumns` and `onVisibleColumnsChange`
   * stay the exact references they were before.
   */
  layoutAware?: boolean;
}

export function useSignalTableModel({ fillHeight, layoutAware = false }: UseSignalTableModelOptions = {}) {
  const identityQuery = useFlowIdentity();
  const identityReady = identityQuery.isSuccess || identityQuery.isError;
  const identity = identityQuery.data ?? null;

  // U7 — the throttled refresh-on-read POST, fired once per mount
  // (StrictMode-safe, `useRefreshSignals.ts`'s doc comment). Independent of
  // `identityReady`/`machine.isReady`: the refresh route scopes itself off
  // the acting user's own auth context, not anything this tab has loaded.
  useRefreshOnMount();

  const viewState = useFlowViewState('signal');
  const rawMachine = useViewStateMachine({
    entity: 'signal',
    presets: SIGNAL_PRESETS,
    defaultPresetId: SIGNAL_LANDING_PRESET_ID,
    ready: identityReady && viewState.isRestored,
    viewState,
  });

  // Signals V3 PR 2, U6 — the `signals` list layout (copied from
  // `useAccountTableModel.tsx`'s layout block). Both hooks run on every
  // render (rules of hooks); without `layoutAware` their results are simply
  // not used, so V1 keeps the raw machine by reference. The wrapped machine
  // reads an unsaved Layout-menu pick as "Modified" and commits it on Save.
  const layout = useFlowListLayout('signals', { viewPin: rawMachine.activeView.flowLayout ?? null });
  const layoutMachine = useFlowLayoutAwareMachine(rawMachine, 'signals', layout);
  const baseMachine = layoutAware ? layoutMachine : rawMachine;

  // "All else": `?exclude_favs=1` asks for My Signals minus the viewer's favorites. A URL param, outside
  // `working`, so it is never Modified, never persisted with the view state, and Reset/restore never see it.
  // Effective only on My Signals; the effect below drops it as soon as any other view is active.
  const [excludeParam, setExcludeParam] = useQueryState('exclude_favs', flowUrlParams.exclude_favs.withOptions({ history: 'replace' }));
  const onLandingView = baseMachine.activeViewId === SIGNAL_LANDING_PRESET_ID;
  const excludeFavorites = excludeParam === '1' && onLandingView;

  // While "All else" is on, nothing may create a saved view from the working state: the exclusion lives in the URL,
  // not in `working`, so a saved copy would silently be plain My Signals. Guarding the machine itself closes every
  // door (inline Save, popover Save/Save as new/New view, the pane's "+"). Copying a STORED view (`saveCopyOf`)
  // does not read the working state and stays allowed.
  const machine = useMemo(
    () => excludeFavorites
      ? {
          ...baseMachine,
          saveBlockedReason: EXCLUDE_FAVORITES_SAVE_BLOCKED,
          save: async () => { toast(EXCLUDE_FAVORITES_SAVE_BLOCKED); return null; },
          saveAsNew: async () => { toast(EXCLUDE_FAVORITES_SAVE_BLOCKED); return null; },
        }
      : baseMachine,
    [baseMachine, excludeFavorites],
  );

  useFlowViewStatePersist({
    stateKey: 'signal',
    enabled: machine.isReady,
    activeViewId: machine.activeViewId,
    working: machine.working,
  });

  const { filter: filters, groupLogic, sort, groupBy, columns: workingColumns, columnWidths: workingWidths } = machine.working;

  // A layout draws its `defaultWidths` under whatever the rep dragged (same
  // contract as `useAccountTableModel`): only dragged widths reach the working
  // state, `storedWidths` drops the defaults the rep never touched. A layout
  // with no signals defaults (CDR, V1) hands the machine's own references through.
  const specWidths = layoutAware ? layout.spec.defaultWidths.signals : undefined;
  const layoutWidths = specWidths !== undefined && Object.keys(specWidths).length > 0 ? specWidths : undefined;
  const columnWidths = useMemo(
    () => (layoutWidths ? withDefaultWidths(layoutWidths, workingWidths) : workingWidths),
    [layoutWidths, workingWidths],
  );
  const { setColumnWidths } = machine;
  const onColumnWidthsChange = useCallback(
    (next: Record<string, number>) => setColumnWidths(layoutWidths ? storedWidths(layoutWidths, next, workingWidths) : next),
    [layoutWidths, setColumnWidths, workingWidths],
  );

  // KTD 4 / R11 — a layout's `fixedColumns` are the REQUIRED set (always
  // drawn, never hideable) and the DEFAULT order. The table draws the view's
  // own working order for the keys it holds, with any fixed key it lacks at its
  // default relative slot, and `working.columns` is never rewritten (no effect,
  // no `setColumns` on mount or on a pick), so landing on Signal Response never
  // reads "Modified". Only a rep reorder commits the full order. With no fixed
  // core (CDR, or V1) every value below is the machine's own reference.
  const fixedColumns = useMemo(
    () => (layoutAware
      ? warnUnknownFixedKeys(layout.spec.fixedColumns?.signals ?? NO_FIXED_COLUMNS, SIGNAL_COLUMN_KEYS)
      : NO_FIXED_COLUMNS),
    [layoutAware, layout.spec],
  );
  const visibleColumns = useMemo(() => fixedColumns.length === 0 && layoutAware
    ? identityFirst(workingColumns, SIGNAL_IDENTITY_COLUMN)
    : mergeFixedColumns(fixedColumns, workingColumns), [fixedColumns, workingColumns, layoutAware]);
  const { setColumns } = machine;
  // The Columns manager (no pinned row) commits the full order, and a header
  // drag emits the whole drawn order. That order is the saved order, with any
  // omitted fixed column re-inserted at its default slot. Nothing on the
  // server or client requires `contact_name` first or present (the saved
  // `columns` is a plain string list membership-checked against the catalog).
  const restoreAndSetColumns = useCallback(
    (next: string[]) => setColumns(restoreWorkingColumns(fixedColumns, next)),
    [setColumns, fixedColumns],
  );
  const onVisibleColumnsChange = fixedColumns.length === 0 ? setColumns : restoreAndSetColumns;

  // Stable empty fallback — `groupLevels` keys the arrangement memos below,
  // and the collapse block in `SignalsFlowTable.tsx`.
  const groupLevels = groupBy ?? NO_GROUP_LEVELS;

  // The omnibox (Command Center's header) handles search on this tab
  // (`config.showSearch: false`) — this local state exists only to satisfy
  // FlowTable's controlled `search`/`onSearchChange` contract and is never
  // driven by any visible input.
  const [search, setSearch] = useState('');

  // Flow-admin "View as" (U15): the one effective viewer (null while impersonating).
  const viewAs = useEffectiveFavoritesViewAs();

  const { isReady: machineReady } = machine;
  const openViewFn = machine.openView;
  const isDirty = machine.isDirty;
  useEffect(() => {
    if (machineReady && excludeParam !== null && !onLandingView) void setExcludeParam(null);
  }, [machineReady, excludeParam, onLandingView, setExcludeParam]);
  const setExcludeFavorites = useCallback(
    async (on: boolean) => {
      if (!on) {
        await setExcludeParam(null);
        return;
      }
      // The remainder is "canonical My Signals minus favorites" (that is what the count measures), so a modified
      // My Signals reopens clean first. Edits made AFTER the chip is on stay allowed and narrow the remainder.
      if (!onLandingView || isDirty) await openViewFn(SIGNAL_LANDING_PRESET_ID, { carry: false });
      await setExcludeParam('1');
    },
    [onLandingView, isDirty, openViewFn, setExcludeParam],
  );

  const query = useFlowSignals({
    search,
    filters,
    groupLogic,
    sort,
    groupBy,
    viewAs,
    excludeFavorites,
    enabled: machine.isReady,
  });

  // The server refuses the exclusion when a favorite filters on Open business hours (a favorite added while the chip is
  // on, a direct `?exclude_favs=1` link, or a click before the counts settled). Recover once per error: drop the chip
  // and land on plain My Signals with the same toast as the client pre-check. The ref guards on the error's IDENTITY and
  // is set before any async work, so StrictMode's setup->cleanup->setup replay cannot toast twice; it is deliberately
  // not reset on mount (that would re-arm the replay), a new rejection is a new error object.
  const unexcludable = excludeFavorites && isUnexcludableFavoritesError(query.error) ? query.error : null;
  const handledUnexcludableRef = useRef<unknown>(null);
  useEffect(() => {
    if (unexcludable === null || handledUnexcludableRef.current === unexcludable) return;
    handledUnexcludableRef.current = unexcludable;
    void setExcludeParam(null);
    if (isDirty) void openViewFn(SIGNAL_LANDING_PRESET_ID, { carry: false });
    toast(ALL_ELSE_UNEXCLUDABLE_TOAST);
  }, [unexcludable, isDirty, openViewFn, setExcludeParam]);

  const readOnly = viewAs !== null;
  const listQueryKey = useMemo(
    () => flowSignalsQueryKey({ search, filters, groupLogic, sort, groupBy, viewAs, excludeFavorites }),
    [search, filters, groupLogic, sort, groupBy, viewAs, excludeFavorites],
  );
  const ranking = useSignalsRanking();

  // QA batch 3 D-A step 4 — a client group level groups over the WHOLE list
  // (FlowTable detects groups by adjacency; arranging only the loaded pages
  // would split groups and under-count every header), so every page loads
  // while one is active — and, since G5, while Type (`rankerGroupOrder.ts`,
  // Ranker-ordered) is a level: the server pages types alphabetically, so
  // the Ranker's #1 type can sit pages deep. Step is Ranker-ordered too but
  // by a fixed label vocabulary, so it orders correctly over whatever is
  // loaded — landing on any preset (including All Signals, grouped by
  // Assignee → Step) shows one page and Load more; a later page may insert
  // a step between two already-loaded ones inside the last loaded rep,
  // accepted (qa-batch4-plan.md D-C, reversing G5 R2). The loop's mechanics
  // (storm, StrictMode, and resuming after a joined page-1 refetch) live in
  // `useAutoLoadAllPages` (fix-plan-batch3.md D-A).
  //
  // fix-plan-batch4.md D-A — the exit half: Local time / Business hours are not in the key, so
  // removing one leaves the SAME entry holding every page the loop fetched (1,000 rows, no Load
  // more — Codex batch-4 F1, checklist 74). When the loop stops filling an entry, that entry goes
  // back to page 1 (an in-flight page is cancelled first; a cancelled invalidation refetch is
  // re-issued for page 1 alone).
  // knv.13 — a browser-evaluated filter (`business_hours`) also needs every page: a match can sit
  // on any page, and the group headers' counts come from the filtered, arranged rows.
  const needsAllPages = groupingNeedsEveryPage(groupLevels) || filterNeedsEveryPage(filters);
  useAutoLoadAllPages(needsAllPages, {
    hasNextPage: !!query.hasNextPage,
    isFetching: query.isFetching,
    isFetchNextPageError: query.isFetchNextPageError,
    pagesLoaded: query.data?.pages.length ?? 0,
    fetchNextPage: query.fetchNextPage,
  });
  useReleaseAutoLoadedPages(needsAllPages, listQueryKey);

  const dayToken = useLocalDayToken();
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- `dayToken` is read only to force a re-derivation at local midnight (real eslint-plugin-react-hooks permits an extra, non-referenced dependency; only oxlint's stricter reimplementation flags it).
  const today = useMemo(() => new Date(), [dayToken]);

  // `?signal=` — mirrors `ContactFlowTable.tsx`'s `?contact=` (:650):
  // the single reader/writer of which row is expanded, shareable and
  // persistent across a refresh. Hoisted above `rawRows` (plan
  // 2026-10-13-001 U1) so the deep-link pin below can key off it; every
  // other hook keeps its original order.
  const [expandedId, setExpandedIdRaw] = useQueryState('signal', flowUrlParams.signal.withOptions({ history: 'replace' }));
  const setInAppExpandedId = useSignalsUiStore(s => s.setInAppExpandedId);
  // The ONE in-app writer of `?signal=` (row click, auto-expand-next, Archive
  // click). Records the id in the store BEFORE the URL write so a remount that
  // finds the same id in the URL knows it was a selection, not a link (R1).
  const setExpandedId = useCallback(
    (id: string | null) => {
      setInAppExpandedId(id);
      return setExpandedIdRaw(id);
    },
    [setInAppExpandedId, setExpandedIdRaw],
  );
  const trackSignalsEvent = useTrackSignalsEvent();
  // U2 (plan 2026-10-13-001) — one-shot Slack "Open in Terra" attribution
  // for a `?signal=…&source=slack_notification` landing; here (not in a
  // renderer) so the V1 and V2 Signals mounts both get it.
  //
  // R1 — the `?signal=` the page MOUNTED with (`useState` initializer,
  // StrictMode-safe). Two uses: `initialExpandedId` is what the V2
  // `SignalsTable` scrolls into view once; `landingSignalId` is the subset
  // that arrived via a LINK (Slack "Open in Terra", legacy `?action=`
  // redirect, state-links "Signals ↗", a shared URL) and is the only id the
  // pin, the missing-toast and the click attribution may act on. An id the
  // model itself wrote (`inAppExpandedId`) that is still in the URL on a
  // remount — switch to Contacts and back — is a selection, not a landing.
  const [initialExpandedId] = useState(() => expandedId);
  const [landingSignalId] = useState(() =>
    expandedId && expandedId !== useSignalsUiStore.getState().inAppExpandedId ? expandedId : null,
  );
  useSlackClickAttribution(landingSignalId);

  // Gong reconcile U20 — a row Trellus just confirmed done (completed, or the
  // rep removed the contact from the flow) disappears at once; the server
  // catches up through the dialer-event hint (`useTrellusCompletionSync`).
  // Subscribed to `tombstones` so the list re-derives the moment one lands.
  // The tombstone carries the completed step; a row whose step is past it is
  // real work again. Remount-proof — nothing per-mount.
  const tombstones = useTrellusHostStore(state => state.tombstones);
  const tombstoneClock = useTombstoneExpiryClock(tombstones);
  // A dated-snooze tombstone expires (`expires_at`); the clock is read when the list re-derives (new data or a new tombstone).
  const rawRows = useMemo(() => {
    const now = Math.max(Date.now(), tombstoneClock);
    return (query.data?.pages ?? [])
      .flatMap(page => page.rows)
      .filter(row => !isTombstonedAtStep({ tombstones }, { signalId: row.signal_id, stepNumber: row.step_number ?? null }, now));
  }, [query.data, tombstones, tombstoneClock]);
  const rankingEntries = useMemo(() => ranking.data?.entries ?? [], [ranking.data]);

  // Notes summaries for the row bubbles (`SignalsTable`'s `rowLead`) — one query
  // per keyset page, same as Companies/People. A row's target is its contact,
  // else its account (`signalNoteTarget`); `useNotesSummaries` lists a target
  // shared by several signals once per page. Empty (no request) while Notes is off.
  const notesPages = useMemo(
    () =>
      (query.data?.pages ?? []).map(page =>
        page.rows.flatMap((row): NoteSummaryItem[] => {
          const target = signalNoteTarget(row);
          return target ? [{ kind: target.kind, id: target.id }] : [];
        }),
      ),
    [query.data],
  );
  const notesSummaries = useNotesSummaries(notesPages);

  const prioritized = useMemo(
    () => prioritizeSignals(rawRows, rankingEntries, today),
    [rawRows, rankingEntries, today],
  );

  // U1 — a `?signal=<id>` deep link (Slack "Open in Terra") onto a row the
  // current view does not contain: fetch it by id as a SIDE query (decision
  // 2 — never `machine.setFilters`, which would persist as a working filter
  // + "Modified" banner), only once the list has settled without it.
  const landingExpanded = !!landingSignalId && expandedId === landingSignalId;
  const expandedInList = landingExpanded && rawRows.some(r => r.signal_id === landingSignalId);
  const pinned = useSignalRow(landingSignalId, {
    enabled: landingExpanded && machine.isReady && !query.isLoading && !expandedInList,
  });
  // Decision 1 — the pinned row bypasses `prioritizeSignals` (it numbers per
  // assignee bucket over everything passed in, so a foreign row would
  // renumber the visible ones) and takes the `priority: null` path closed
  // rows already take; the arranger sinks it like any null. Keyed on
  // the landing id: the pin exists only while the landing row is the expanded
  // one, so expanding another row drops it and an in-app row the rep later
  // filters out is never pinned (R1). Deduped by `signal_id`, so a later page
  // that loads it shows it once.
  const pinnedRow = pinned.row;
  const withPin = useMemo<Array<PrioritizedSignalRow & { pinned_from_link?: boolean }>>(() => {
    if (!landingExpanded || !pinnedRow || pinnedRow.signal_id !== landingSignalId) return prioritized;
    if (prioritized.some(r => r.signal_id === pinnedRow.signal_id)) return prioritized;
    // Same Trellus-done rule as the list: a deep-linked row the dialer already
    // confirmed done must not come back through the pin.
    if (isTombstonedAtStep({ tombstones }, { signalId: pinnedRow.signal_id, stepNumber: pinnedRow.step_number ?? null }, Math.max(Date.now(), tombstoneClock))) {
      return prioritized;
    }
    return [{ ...pinnedRow, priority: null, pinned_from_link: true }, ...prioritized];
  }, [prioritized, pinnedRow, landingExpanded, landingSignalId, tombstones, tombstoneClock]);

  // U1 — the id is gone from Terra (deactivated / outside the 90-day arm
  // window): tell the rep once per id and leave them on the tab with
  // `?signal=` as-is. The id is recorded in the store synchronously BEFORE
  // the toast, so StrictMode's effect double-invoke (or a remount) no-ops
  // (docs/v4-system/strictmode-resilient-effects.md).
  const markMissingSignalToasted = useSignalsUiStore(s => s.markMissingSignalToasted);
  // `!isPlaceholderData`: `useFlowSignals` keeps the previous key's data
  // (`keepPreviousData`), so on an id change the pin query briefly reports
  // `isSuccess` with the PRIOR id's (possibly empty) result — that must not
  // toast the new id as missing before its own fetch lands.
  const pinSettledEmpty = pinned.isSuccess && !pinned.isPlaceholderData && pinned.row === null;
  useEffect(() => {
    if (!landingExpanded || !landingSignalId || !pinSettledEmpty || expandedInList) return;
    if (useSignalsUiStore.getState().missingSignalToastIds.has(landingSignalId)) return;
    markMissingSignalToasted(landingSignalId);
    toast("That signal isn't in Terra any more");
  }, [landingExpanded, landingSignalId, pinSettledEmpty, expandedInList, markMissingSignalToasted]);
  // R24 — `localTimeFor` reads the wall clock (`business-hours.ts` formats
  // a `now` in the contact's zone), so a memo keyed only on the row set
  // froze the badge, its business-hours colour and the Local-time sort for
  // as long as the tab stayed open on an unchanged list — which is how reps
  // use it. `minuteToken` re-derives once a minute and immediately on
  // return to a backgrounded tab. Unlike `dayToken` above, this dependency
  // is genuine, not just an extra: the callback below builds `now` from it
  // and passes that into `localTimeFor`, which threads it through to the
  // business-hours math — so `minuteToken` is actually read, not merely
  // listed, and no exhaustive-deps suppression is needed.
  // Stage 4 perf budget (bead terra-design-v2-table-tweaks-at4.20) — KNOWN,
  // OUT-OF-SCOPE GAP, recorded rather than forced through (the bead's own
  // "if a mitigation needs a V1 test edit, stop and record it" rule):
  // `minuteToken` ticking rebuilds BOTH `withLocalTime` (below) and `config`
  // (`buildSignalsTableConfig`'s memo, further down) every minute, so V2's
  // memoized `LedgerRow` re-renders every currently-visible row on every
  // tick even when neither grouping nor sorting touches `local_time`/
  // `business_hours` — not just the rows whose displayed local-time text
  // actually changed. The two mitigations the bead lays out (keep `now` out
  // of `config`'s deps via a context/ref read inside `SignalRowActions`/the
  // `local_time` cell instead of a prop or baked-in row field; only rebuild
  // `withLocalTime` when a level/sort actually needs it) both require the
  // LOCAL_TIME COLUMN's own display to stop depending on a value baked into
  // the row array — but `signals/__tests__/SignalsFlowTable.localTime.test.tsx`
  // ("re-renders the badge a minute later, with the row set unchanged")
  // pins that today's badge ticks every minute even grouped by NEITHER
  // `local_time` nor `business_hours`, so decoupling it is a real,
  // multi-file V1 rendering-path change (`signalsTableConfig.tsx`'s and
  // `shell-next/tables/signalsColumns.tsx`'s `local_time` renderers,
  // `SignalRowActions`'s `now` prop, and every suite that exercises
  // freshness/local-time through them) — its own bead, not a fragment of
  // this mount. `SignalRowActions` IS memoized (mitigation 3,
  // `signals/SignalRowActions.tsx`), which helps whenever `config` rebuilds
  // for a reason OTHER than this tick (e.g. `rows` identity changing on an
  // unrelated refetch) without every row's OWN props changing too.
  const minuteToken = useMinuteToken();
  // QA batch 2, D-A/B6 — hoisted out of the memo below so `buildSignalsTableConfig`
  // can share this SAME minute-fresh clock for `getSignalRowActions`'s
  // verification-freshness check (`SignalRowActions.tsx`'s `now` prop),
  // rather than a second, slightly-skewed `new Date()` read.
  const now = useMemo(() => new Date(minuteToken), [minuteToken]);
  // Signals V3 PR 2, U4 — the day-level "Step since" rides the same memo,
  // keyed on the midnight-granular `today` (the source is a calendar date).
  const withLocalTime = useMemo<SignalRowWithComputed[]>(
    () => withPin.map(row => {
      const stepSince = computeStepSince(row.step_started_at, today);
      return { ...row, local_time: localTimeFor(row, now), step_since: stepSince.label, step_since_days: stepSince.days };
    }),
    [withPin, now, today],
  );
  // knv.13 — browser-evaluated filters (clientFilters.ts) run after `local_time` is stamped and
  // before arrangement, so group counts and Load more reflect what is shown.
  const clientFiltered = useMemo(() => applyClientSignalFilters(withLocalTime, filters), [withLocalTime, filters]);
  // D10 / QA batch 3 D-A step 3 — ONE client arrangement, always applied:
  // server levels keep the server's group order (each group placed at its
  // first row in server order), the client levels (`local_time`,
  // `business_hours` — never sent to the server) group in the browser, and
  // a client sort (Priority / Local time, which the server replaces with its
  // `event_date` default) orders rows inside the innermost group. With no
  // levels and a server sort it is the identity. Group values go through
  // the SAME `signalGroupValue` FlowTable's `config.groupValue` uses, so a
  // row's position always matches the header it renders under.
  //
  // fix-plan-batch3.md D-E — `signalGroupValue` no longer takes a `now`
  // (the hour-bucket label is a precomputed, UTC-fixed 24-entry table, not
  // a clock read), so it's passed directly rather than wrapped in a
  // `useCallback` keyed on `now`.
  //
  // QA batch 3 G5 — Type and Step levels (and any server level under a Type
  // level) order the way Action View does, not alphabetically: the arranger's
  // per-level `serverLevelRank` hook, filled by `buildRankerLevelOverrides`
  // (`rankerGroupOrder.ts`). While the Ranker is loading or failed,
  // `rankingEntries` is `[]` and the order degrades to min priority → size →
  // server order (D-F); the memo re-arranges when the entries arrive.
  //
  // fix-plan-batch3.md D-C — a group is its header text at every level, so
  // two runs the server emitted apart (two SFDC-id flows → `Unresolved
  // Flow`; `''` and NULL Type → `No Type`) merge into one group; inside such
  // a MERGED leaf only, rows are re-sorted by the active server sort
  // (`serverSortCompare`), while every other leaf keeps the server's order
  // untouched.
  const canonical = useMemo(() => buildCanonicalRanking(rankingEntries), [rankingEntries]);
  const rankerOverrides = useMemo(
    () => buildRankerLevelOverrides(groupLevels, signalGroupValue, canonical),
    [groupLevels, canonical],
  );
  const serverSortCompare = useMemo(
    () => buildFieldComparator<SignalRowWithComputed>(sort, SIGNAL_FIELDS.find(f => f.key === sort.field)?.type ?? 'text'),
    [sort],
  );
  const rows = useMemo(
    () => arrangeSignalsClientSide(
      clientFiltered,
      groupLevels,
      sort,
      signalGroupValue,
      signalClientGroupRank,
      { serverLevelRank: rankerOverrides, serverSortCompare },
    ),
    [clientFiltered, groupLevels, sort, rankerOverrides, serverSortCompare],
  );

  // `Action View Loaded` — once per mount, after the first successful page
  // with the view machine ready. The ref is a guard that is never reset
  // (same shape as `useTrackViewViewed`): StrictMode's double effect run at
  // mount sees `query` still loading, or the already-set flag, so it can
  // never double-fire, and a refetch / view switch never re-fires it.
  const loadedTrackedRef = useRef(false);
  const activeViewId = machine.activeViewId;
  const listSettled = query.isSuccess && machine.isReady;
  const loadedRowCount = rows.length;
  useEffect(() => {
    if (loadedTrackedRef.current || !listSettled || !activeViewId) return;
    loadedTrackedRef.current = true;
    trackSignalsEvent(ANALYTICS_EVENTS.ACTION_VIEW_LOADED, { tab: activeViewId, actionCount: loadedRowCount });
  }, [listSettled, activeViewId, loadedRowCount, trackSignalsEvent]);

  const { openView } = machine;
  const handleViewChange = useCallback(
    (viewId: string) => {
      trackSignalsEvent(ANALYTICS_EVENTS.ACTION_VIEW_TAB_CHANGED, { tab: viewId });
      return openView(viewId);
    },
    [trackSignalsEvent, openView],
  );

  const pendingExpandId = useSignalsUiStore(s => s.pendingExpandId);
  const setPendingExpandId = useSignalsUiStore(s => s.setPendingExpandId);
  const requestArchive = useSignalsUiStore(s => s.requestArchive);

  // Auto-expand-next (bead .16, plan D14/D18) — mirrors Action View's
  // `ActionQueuePage.tsx:205-267` auto-unfurl-next over
  // `grouping.ts:433`'s `nextVisibleActionId`, reproduced here over THIS
  // table's own FINAL client-sorted `rows` array (already computed above —
  // the exact sequence `FlowTable` iterates to build group headers/rows).
  // `FlowTable` doesn't expose its internal collapsed-group filtering, so
  // per the bead's own fallback this is simply "the next displayed row" in
  // that array, not a collapse-aware skip. Computed synchronously from the
  // CURRENT `rows` closure, before the mutation's own cache invalidation
  // (which runs in the caller, asynchronously) can change it.
  const handleActed = useCallback(
    (id: string) => {
      const index = rows.findIndex(r => r.signal_id === id);
      const nextId = index === -1 ? null : rows[index + 1]?.signal_id ?? null;
      void setExpandedId(nextId);
      setPendingExpandId(nextId);
    },
    [rows, setExpandedId, setPendingExpandId],
  );

  // Scrolls the newly auto-expanded row into view exactly once — guarded by
  // the store's `pendingExpandId` (not a lifetime ref, per the bead's own
  // Do-Not-Create list) so a normal row click (which also changes
  // `expandedId` but never touches the store) never triggers a scroll.
  useEffect(() => {
    if (!expandedId || expandedId !== pendingExpandId) return;
    document.querySelector(`[data-row-id="${expandedId}"]`)?.scrollIntoView({ block: 'nearest' });
    setPendingExpandId(null);
  }, [expandedId, pendingExpandId, setPendingExpandId]);

  // QA batch 2, D-I — a row's (or the Timeline's) Archive click expands the
  // row (so the Signal card mounts) and requests the archive-form intent
  // (`useSignalsUiStore`'s one-shot `archiveIntent`; `SignalActionCard`
  // reads it and opens its inline form). Mirrors `handleActed`'s shape.
  const handleArchiveRequest = useCallback(
    (signalId: string) => {
      void setExpandedId(signalId);
      requestArchive(signalId);
    },
    [setExpandedId, requestArchive],
  );

  // Signals V3 PR 2, U10 (KTD 8) — row selection exists only under the
  // Signal Response layout: component state (`useSignalSelection`, never a
  // store), handed to the ledger as `config.selection` (it draws the tight
  // checkbox column and owns Cmd/Shift-click). The hook always runs (rules of
  // hooks); CDR and V1 leave `selection` undefined, so no checkbox column.
  const signalSelection = useSignalSelection(rows);
  // While viewing as a rep every write is off, so no selection (hence no bulk actions) either.
  const selection = layoutAware && layout.id === 'signal-response' && !readOnly ? signalSelection : undefined;

  const config = useMemo(() => {
    const base = buildSignalsTableConfig(today, { onActed: handleActed, onArchiveRequest: handleArchiveRequest, now, readOnly });
    return selection ? { ...base, selection } : base;
  }, [today, handleActed, handleArchiveRequest, now, selection, readOnly]);

  // The ONE emit site for `Action Expanded`: the table's own toggle (a real row
  // / card click), which hands us just the id — the row is looked up here.
  // Programmatic expands (`handleActed` auto-expand-next, `handleArchiveRequest`)
  // go through `setExpandedId` directly and never emit. Only `signal_action:`
  // ids qualify (the event requires a `signalActionId`), and collapsing or
  // re-selecting the open row is silent.
  const handleRowExpandedIdChange = useCallback(
    (id: string | null) => {
      if (id !== null && id !== expandedId && signalActionRecordId(id) !== null) {
        const row = rows.find(r => r.signal_id === id);
        trackSignalsEvent(ANALYTICS_EVENTS.ACTION_EXPANDED, {
          signalId: id,
          signalType: row?.signal_type ?? null,
          assigneeEmail: row?.assignee_email ?? null,
        });
      }
      return setExpandedId(id);
    },
    [expandedId, rows, trackSignalsEvent, setExpandedId],
  );

  const tableProps: Omit<FlowTableProps<SignalRowWithComputed>, 'toolbarTrailing' | 'groupCollapseCommand'> = {
    views: SIGNAL_PRESETS,
    viewMachine: machine,
    config,
    rows,
    fillHeight,
    // A layout-aware table also waits for its layout: first paint under a
    // not-yet-loaded layout would draw the CDR columns, then flip. The fetch is not gated.
    // Under a server-rejected exclusion the follow-up plain request is already on its way: no error/Retry flash.
    isLoading: !machine.isReady || (layoutAware && !layout.isLoaded) || query.isLoading || unexcludable !== null,
    isRefetching: query.isPlaceholderData || (query.isFetching && !query.isFetchingNextPage && !query.isLoading),
    error: unexcludable ? null : query.error ? query.error.message : null,
    hasMore: !!query.hasNextPage,
    onLoadMore: () => query.fetchNextPage(),
    isLoadingMore: query.isFetchingNextPage,
    // Bead .20 (S4-U4) — `LedgerTable` only renders a Retry button under an
    // error when `onRetry` is present (`LedgerTable.tsx:650`); without it a
    // 503 shows the server title with no way to recover. Same shape as
    // `useAccountTableModel.tsx:271` / `useContactTableModel.tsx:1002`.
    onRetry: () => query.refetch(),
    activeViewId: machine.activeViewId,
    onViewChange: handleViewChange,
    sort,
    onSortChange: machine.setSort,
    filters,
    onFiltersChange: machine.setFilters,
    groupLogic,
    onGroupLogicChange: machine.setGroupLogic,
    search,
    onSearchChange: setSearch,
    groupBy,
    onGroupByChange: machine.setGroupBy,
    visibleColumns,
    onVisibleColumnsChange,
    columnWidths,
    onColumnWidthsChange: layoutWidths ? onColumnWidthsChange : setColumnWidths,
    totalLoaded: rows.length,
    identity,
    expandedId,
    onExpandedIdChange: handleRowExpandedIdChange,
  };

  // `onActed` (bead terra-design-v2-drilldowns-ppn.14) — exposed so the V2
  // `SignalsTable` mount can thread the SAME auto-expand-next callback
  // (`handleActed`, above) into its own `renderDetail` override
  // (`SignalBandDetail`) that `buildSignalsTableConfig`'s `renderDetail`
  // already closes over for V1's `SignalDetail`. Purely additive — no
  // existing destructurer of this hook's return value is affected.
  //
  // `layout` / `fixedColumns` (Signals V3 PR 2, U6) — the V2 mount's
  // renderer decorator reads the layout, and its toolbar hands the Columns
  // manager the fixed core as locked rows (empty when the layout fixes
  // nothing). V1 ignores both.
  return {
    machine,
    identity,
    rows,
    notesSummaries,
    now,
    today,
    groupLevels,
    tableProps,
    onActed: handleActed,
    onArchiveRequest: handleArchiveRequest,
    landingSignalId,
    initialExpandedId,
    layout,
    /** The layout's validated fixed core (empty for CDR / V1); the Columns manager shows these locked. */
    fixedColumns,
    /** U10 — Signal Response's row selection (for the toolbar's Bulk actions); undefined otherwise. */
    selection,
    /** U15 — true while viewing as a rep: every write affordance is disabled. */
    readOnly,
    /** "All else": the list excludes the rows the viewer's favorites include (My Signals only, URL-only state). */
    excludeFavorites,
    setExcludeFavorites,
  };
}

